<script setup lang="ts">
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../dialog';

import {
  DialogContent,
  DialogClose,
  DialogPortal,
} from 'radix-vue';

defineProps<{
  popUpLink?: string;
  title: string;
  description: string;
  ctaText: string;
  /** Icon drawn on top of the blob; both are needed for the illustration to render. */
  illustrationIcon?: string;
  illustrationBlob?: string;
  /** Drives the blob colour, and turns the icon red for `urgent`. */
  illustrationTone?: 'default' | 'unavailable' | 'urgent';
  /** Extra copy under the description, on the same grey surface the bag page uses. */
  note?: string;
  triggerStyle?: string;
  wrapperStyle?: string;
  titleStyle?: string;
  descriptionStyle?: string;
  ctaStyle?: string;
}>();
</script>

<template>
  <Dialog>
    <DialogTrigger>
      <slot name="trigger">
        <a :class="triggerStyle || 's-link'">
          {{ popUpLink }}
        </a>
      </slot>
    </DialogTrigger>
    <DialogHeader>
      <!--
        Portalled to the body: the tag pop up is rendered inside the bag
        header, whose badge and logo layers sit in their own stacking context,
        so anything left in place is painted under the logo.
      -->
      <DialogPortal>
        <DialogContent
          :class="wrapperStyle || 's-pop-up__wrapper'"
        >
        <DialogTitle :class="titleStyle || 's-pop-up__title'">
          {{ title }}
        </DialogTitle>
        <span
          v-if="illustrationIcon"
          class="s-pop-up__illustration"
          :class="`s-pop-up__illustration--${illustrationTone || 'default'}`"
          aria-hidden="true"
        >
          <span v-if="illustrationBlob" class="s-pop-up__illustration__blob" :style="{ '--blob-image': `url(&quot;${illustrationBlob}&quot;)` }" />
          <img v-if="!illustrationBlob" :src="illustrationIcon" alt="" class="s-pop-up__illustration__icon--image" />
          <span v-else class="s-pop-up__illustration__icon" :style="{ '--icon-image': `url(&quot;${illustrationIcon}&quot;)` }" />
        </span>

        <DialogDescription :class="descriptionStyle || 'text-mauve11 mt-[10px] mb-5 text-[15px] leading-normal text-center'">
          {{ description }}
        </DialogDescription>
        <p v-if="note" class="s-pop-up__note">
          {{ note }}
        </p>
        <DialogClose :class="ctaStyle || 's-button'" class="amp-auto-track" :data-analytics-click-behaviour="AnalyticsClickBehaviour.ModalClose">
          {{ ctaText }}
        </DialogClose>
        </DialogContent>
      </DialogPortal>
    </DialogHeader>
  </Dialog>
</template>

<style scoped>
@import '../../../assets/css/mixins/_mixins.index.pcss';

.s-pop-up {
  &__illustration {
    position: relative;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 80px;
    margin: theme('spacing.16') theme('spacing.0') theme('spacing.8') theme('spacing.0');

    &__blob,
    &__icon {
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: contain;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-position: center;
      -webkit-mask-size: contain;
    }

    &__blob {
      position: absolute;
      inset: 0;
      mask-image: var(--blob-image);
      -webkit-mask-image: var(--blob-image);
      background-color: theme('colors.beige.30');
    }

    &__icon {
      position: relative;
      width: 75px;
      height: 75px;
      mask-image: var(--icon-image);
      -webkit-mask-image: var(--icon-image);
      background-color: theme('colors.primary.30');
    }

    &__icon--image {
      position: relative;
      width: 120px;
      height: 120px;
      object-fit: contain;
    }

    &--unavailable {
      .s-pop-up__illustration__blob {
        background-color: #F4F6F6;
      }

      .s-pop-up__illustration__icon {
        background-color: #5F6D6D;
      }
    }

    &--urgent {
      .s-pop-up__illustration__blob {
        background-color: theme('colors.red.10');
      }

      .s-pop-up__illustration__icon {
        background-color: theme('colors.red.40');
      }
    }
  }

  &__note {
    background-color: theme('colors.neutral.20');
    padding: theme('spacing.8');
    border-radius: theme('borderRadius.md');
    color: black;
  }

  &__title {
    @mixin h5-tenon;
    color: black;
    font-weight: bold;
    align-self: center;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: theme('colors.neutral.10');
    position: fixed;
    top: 50%;
    left: 50%;
    width: 70%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    padding: 25px;
    box-shadow: 0px 10px 38px -10px hsl(206, 22%, 7%, 0.35), 0px 10px 20px -15px hsl(206, 22%, 7%, 0.2);
    z-index: theme('zIndex.modal');

    @screen tablet-wide {
      max-width: 300px;
    }
  }
}

.s-link {
  color: var(--theme-heading-color-app);
  text-decoration: underline;
}

.s-button {
  @mixin cta-app;
  @mixin body;
  width: fit-content;
  padding: 4px 20px;
  align-self: center;
  margin-top: 20px;
}
</style>
